import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Download,
} from 'lucide-react';
import { commissionsApi } from '@/api/commissions.api';
import { useAgentStore } from '@/store/agentStore';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { AgentCommission } from '@/types/commission.types';
import { AgentHeader } from '@/components/layout/AgentHeader';
import { AgentSidebar } from '@/components/layout/AgentSidebar';
import { BottomNav } from '@/components/layout/BottomNav';
import toast from 'react-hot-toast';

type CommissionStatus = 'PENDING' | 'PAID' | 'WITHHELD';
type Period = 'today' | 'week' | 'month' | 'all';

export default function CommissionsPage() {
  const { agent } = useAgentStore();
  const [selectedStatus, setSelectedStatus] = useState<CommissionStatus | 'all'>('all');
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('month');
  const [page, setPage] = useState(1);

  // Query para resumen de comisiones
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['commissionsSummary', selectedPeriod],
    queryFn: () => commissionsApi.getSummary(selectedPeriod),
  });

  // Query para lista de comisiones
  const { data: commissions, isLoading: listLoading } = useQuery({
    queryKey: ['commissionsList', selectedStatus, page],
    queryFn: () =>
      commissionsApi.getList({
        status: selectedStatus === 'all' ? undefined : selectedStatus,
        page,
        limit: 20,
      }),
  });

  // Compute summary from list data when earnings API returns zeros
  const computedSummary = commissions?.data?.length
    ? {
        pending: commissions.data.filter(c => c.status === 'PENDING').reduce((s, c) => s + c.amount, 0),
        pendingCount: commissions.data.filter(c => c.status === 'PENDING').length,
        paid: commissions.data.filter(c => c.status === 'PAID').reduce((s, c) => s + c.amount, 0),
        paidCount: commissions.data.filter(c => c.status === 'PAID').length,
        withheld: commissions.data.filter(c => c.status === 'WITHHELD').reduce((s, c) => s + c.amount, 0),
        withheldCount: commissions.data.filter(c => c.status === 'WITHHELD').length,
        total: commissions.data.reduce((s, c) => s + c.amount, 0),
        totalCount: commissions.data.length,
      }
    : null;

  // Use API summary if it has real data, otherwise fall back to computed from list
  const effectiveSummary = (summary && summary.total > 0) ? summary : (computedSummary ?? summary);

  const handleExport = async () => {
    try {
      toast.loading('Exportando comisiones...');
      const blob = await commissionsApi.exportToCSV({
        status: selectedStatus === 'all' ? undefined : selectedStatus,
        period: selectedPeriod,
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `comisiones-${agent?.agentCode}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.dismiss();
      toast.success('Comisiones exportadas exitosamente');
    } catch (error) {
      toast.dismiss();
      toast.error('Error al exportar comisiones');
      // export error handled by toast
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AgentHeader />

      <div className="flex">
        <AgentSidebar />

        <main className="flex-1 pb-20 lg:pb-6">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <DollarSign className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Comisiones</h1>
                    <p className="text-sm text-gray-600">Ganancias y pagos</p>
                  </div>
                </div>
                <button
                  onClick={handleExport}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Exportar</span>
                </button>
              </div>
            </div>
          </header>

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Summary Cards */}
            {summaryLoading && !effectiveSummary ? (
              <SummarySkeleton />
            ) : effectiveSummary ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                {/* Pendiente */}
                <SummaryCard
                  title="Pendiente"
                  amount={effectiveSummary.pending}
                  icon={<Clock className="w-6 h-6 text-yellow-600" />}
                  bgColor="bg-yellow-50"
                  iconBg="bg-yellow-100"
                  textColor="text-yellow-900"
                  count={effectiveSummary.pendingCount}
                />

                {/* Pagado */}
                <SummaryCard
                  title="Pagado (Mes)"
                  amount={effectiveSummary.paid}
                  icon={<CheckCircle className="w-6 h-6 text-green-600" />}
                  bgColor="bg-green-50"
                  iconBg="bg-green-100"
                  textColor="text-green-900"
                  count={effectiveSummary.paidCount}
                />

                {/* Retenido */}
                <SummaryCard
                  title="Retenido"
                  amount={effectiveSummary.withheld}
                  icon={<XCircle className="w-6 h-6 text-red-600" />}
                  bgColor="bg-red-50"
                  iconBg="bg-red-100"
                  textColor="text-red-900"
                  count={effectiveSummary.withheldCount}
                />

                {/* Total */}
                <SummaryCard
                  title="Total Ganado"
                  amount={effectiveSummary.total}
                  icon={<TrendingUp className="w-6 h-6 text-blue-600" />}
                  bgColor="bg-blue-50"
                  iconBg="bg-blue-100"
                  textColor="text-blue-900"
                  count={effectiveSummary.totalCount}
                />
              </div>
            ) : null}

            {/* Filters */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                {/* Status Filter */}
                <div className="flex-1 w-full sm:w-auto">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { value: 'all', label: 'Todas' },
                      { value: 'PENDING', label: 'Pendiente' },
                      { value: 'PAID', label: 'Pagado' },
                      { value: 'WITHHELD', label: 'Retenido' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSelectedStatus(option.value as any);
                          setPage(1);
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          selectedStatus === option.value
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Period Filter */}
                <div className="flex-1 w-full sm:w-auto">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Período</label>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { value: 'today', label: 'Hoy' },
                      { value: 'week', label: 'Semana' },
                      { value: 'month', label: 'Mes' },
                      { value: 'all', label: 'Todo' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setSelectedPeriod(option.value as Period)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          selectedPeriod === option.value
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Commissions List */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Historial de Comisiones</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {commissions?.total || 0} comisiones encontradas
                </p>
              </div>

              {listLoading ? (
                <ListSkeleton />
              ) : commissions && (commissions.data ?? []).length > 0 ? (
                <>
                  {/* Desktop Table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Fecha
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Transacción
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Tipo
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            Monto Base
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            Comisión
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                            Estado
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {(commissions.data ?? []).map((commission) => (
                          <tr key={commission.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {format(new Date(commission.createdAt), 'dd MMM yyyy HH:mm', {
                                locale: es,
                              })}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {commission.transaction.transactionId}
                              </div>
                              <div className="text-xs text-gray-500">
                                {commission.transaction.customerPhone || 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  commission.transaction.type === 'CASH_IN'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {commission.transaction.type === 'CASH_IN' ? 'Depósito' : 'Retiro'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              ${commission.transaction.amount.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                              ${commission.amount.toLocaleString()}
                              <div className="text-xs text-gray-500 font-normal">
                                {commission.rate}%
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <CommissionStatusBadge status={commission.status} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="md:hidden divide-y divide-gray-200">
                    {(commissions.data ?? []).map((commission) => (
                      <CommissionCard key={commission.id} commission={commission} />
                    ))}
                  </div>

                  {/* Pagination */}
                  {(commissions.totalPages ?? 1) > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Anterior
                      </button>
                      <span className="text-sm text-gray-700">
                        Página {page} de {commissions.totalPages ?? 1}
                      </span>
                      <button
                        onClick={() => setPage((p) => Math.min(commissions.totalPages ?? 1, p + 1))}
                        disabled={page === (commissions.totalPages ?? 1)}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Siguiente
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="px-6 py-12 text-center">
                  <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">No hay comisiones registradas</p>
                  <p className="text-sm text-gray-500">
                    Las comisiones aparecerán aquí cuando realices transacciones
                  </p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      <BottomNav />
    </div>
  );
}

// Componente SummaryCard
interface SummaryCardProps {
  title: string;
  amount: number;
  icon: React.ReactNode;
  bgColor: string;
  iconBg: string;
  textColor: string;
  count?: number;
}

function SummaryCard({
  title,
  amount,
  icon,
  bgColor,
  iconBg,
  textColor,
  count,
}: SummaryCardProps) {
  return (
    <div className={`${bgColor} rounded-lg p-6 border border-gray-200`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`${iconBg} p-3 rounded-lg`}>{icon}</div>
        {count !== undefined && (
          <span className="text-sm text-gray-600">
            {count} {count === 1 ? 'comisión' : 'comisiones'}
          </span>
        )}
      </div>
      <div>
        <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
        <p className={`text-2xl font-bold ${textColor}`}>${(amount ?? 0).toLocaleString()}</p>
      </div>
    </div>
  );
}

// Componente CommissionStatusBadge
function CommissionStatusBadge({ status }: { status: CommissionStatus }) {
  const config = {
    PENDING: {
      label: 'Pendiente',
      className: 'bg-yellow-100 text-yellow-800',
      icon: <Clock className="w-3 h-3" />,
    },
    PAID: {
      label: 'Pagado',
      className: 'bg-green-100 text-green-800',
      icon: <CheckCircle className="w-3 h-3" />,
    },
    WITHHELD: {
      label: 'Retenido',
      className: 'bg-red-100 text-red-800',
      icon: <XCircle className="w-3 h-3" />,
    },
  };

  const { label, className, icon } = config[status];

  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}
    >
      {icon}
      {label}
    </span>
  );
}

// Componente CommissionCard (Mobile)
function CommissionCard({ commission }: { commission: AgentCommission }) {
  return (
    <div className="p-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                commission.transaction.type === 'CASH_IN'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {commission.transaction.type === 'CASH_IN' ? 'Depósito' : 'Retiro'}
            </span>
            <CommissionStatusBadge status={commission.status} />
          </div>
          <p className="text-xs text-gray-600 font-mono">
            {commission.transaction.transactionId}
          </p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-gray-900">${commission.amount.toLocaleString()}</p>
          <p className="text-xs text-gray-500">{commission.rate}%</p>
        </div>
      </div>
      <div className="flex items-center justify-between text-xs text-gray-600">
        <span>{commission.transaction.customerPhone || 'N/A'}</span>
        <span>{format(new Date(commission.createdAt), 'dd/MM/yyyy HH:mm')}</span>
      </div>
    </div>
  );
}

// Skeletons
function SummarySkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6 animate-pulse">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="bg-gray-100 rounded-lg h-32" />
      ))}
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="divide-y divide-gray-200 animate-pulse">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="px-6 py-4 flex items-center gap-4">
          <div className="h-10 w-10 bg-gray-200 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
          </div>
          <div className="h-6 w-20 bg-gray-200 rounded" />
        </div>
      ))}
    </div>
  );
}
